/**
 * What an operator should know before a quote is generated from the RFQ:
 * the items that have nothing picked (they would be left off the quote)
 * and the vendors asked who have not answered yet.
 */
import type { RfqLine, RfqLineOptions, RfqVendorRequestRow } from "./api";

export type QuoteReadiness = {
  /** Items with no exact match and no replacement picked: left out of the quote. */
  leftOut: { lineId: string; label: string }[];
  vendorsAsked: number;
  vendorsAnswered: number;
  /** Vendors still to answer, by name. */
  vendorsPending: string[];
  deadlineAt: string | null;
};

export const lineLabelOf = (line: RfqLine, index: number): string => {
  const dims = line.dimensions || {};
  const size = [dims.diameter && `${dims.diameter} dia`, dims.thickness, dims.width, dims.length]
    .filter(Boolean)
    .join(" x ");
  return [line.material, line.shape, size].filter(Boolean).join(" ") || `Item ${index + 1}`;
};

export const quoteReadinessOf = (
  lines: RfqLine[],
  matches: RfqLineOptions[] | null,
  vendorRequests: RfqVendorRequestRow[] | null,
  deadlineAt: string | null,
): QuoteReadiness => {
  const leftOut: QuoteReadiness["leftOut"] = [];
  if (matches) {
    lines.forEach((line, index) => {
      const entry = matches.find((m) => m.lineId === line.id);
      const covered = !!line.preferredProductId || (entry ? entry.verdict === "exact" : false);
      if (!covered) leftOut.push({ lineId: line.id, label: lineLabelOf(line, index) });
    });
  }
  const asked = (vendorRequests || []).filter((r) => r.status !== "closed" || !!r.respondedAt);
  const answered = asked.filter((r) => r.status === "responded" || r.status === "declined" || !!r.respondedAt);
  const pending = asked.filter((r) => !answered.includes(r));
  return {
    leftOut,
    vendorsAsked: asked.length,
    vendorsAnswered: answered.length,
    vendorsPending: pending.map((r) => r.supplierName),
    deadlineAt,
  };
};

/** Something the operator should confirm before generating. */
export const quoteNeedsConfirmation = (r: QuoteReadiness): boolean =>
  r.leftOut.length > 0 || (r.vendorsAsked > 0 && r.vendorsAnswered === 0);
